import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type CollectionInsert = Database["public"]["Tables"]["collections"]["Insert"];
type CollectionUpdate = Database["public"]["Tables"]["collections"]["Update"];

// Slim column list for the dashboard card grid. The full row carries
// Json blobs (card_design_json, rsvp_questions, preferences_*) that
// would add hundreds of KB for users with many cards but aren't read
// anywhere on the dashboard.
const DASHBOARD_COLLECTION_SELECT =
  "id,organizer_id,title,recipient_name,share_slug,status,product_image,product_name,total_collected,type,pool_type,occasion,created_at,card_templates(image_url)";

export async function getCollectionBySlug(slug: string) {
  const { data, error } = await supabase
    .from("collections")
    .select("*")
    .eq("share_slug", slug)
    .maybeSingle();
  return { data, error };
}

export async function getCollectionsByOrganizer(organizerId: string) {
  const { data, error } = await supabase
    .from("collections")
    .select(DASHBOARD_COLLECTION_SELECT)
    .eq("organizer_id", organizerId)
    .order("created_at", { ascending: false });
  return { data, error };
}

export async function createCollection(collection: CollectionInsert) {
  const { data, error } = await supabase
    .from("collections")
    .insert(collection)
    .select("id, share_slug")
    .single();
  return { data, error };
}

export async function updateCollection(id: string, updates: CollectionUpdate | Record<string, unknown>) {
  const { data, error } = await supabase
    .from("collections")
    .update(updates as CollectionUpdate)
    .eq("id", id);
  return { data, error };
}

export async function deleteCollection(id: string) {
  const { error } = await supabase
    .from("collections")
    .delete()
    .eq("id", id);
  return { error };
}

export async function getParticipatingCollections(userEmail: string) {
  // Get collection IDs where user sent a message, contributed, or RSVP'd
  const [messagesResult, contributionsResult, rsvpsResult] = await Promise.all([
    supabase.from("messages").select("collection_id").eq("contributor_email", userEmail),
    supabase.from("contributions").select("collection_id").eq("contributor_email", userEmail),
    supabase.from("rsvps").select("collection_id").eq("guest_email", userEmail),
  ]);

  const idSet = new Set<string>();
  (messagesResult.data || []).forEach((r) => idSet.add(r.collection_id));
  (contributionsResult.data || []).forEach((r) => idSet.add(r.collection_id));
  (rsvpsResult.data || []).forEach((r) => idSet.add(r.collection_id));

  const ids = Array.from(idSet);
  if (ids.length === 0) return { data: [], error: null };

  const { data, error } = await supabase
    .from("collections")
    .select(DASHBOARD_COLLECTION_SELECT)
    .in("id", ids)
    .order("created_at", { ascending: false });

  return { data, error };
}
